'use client';

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { TRY_IT, tryItHref } from '../../../../config/try-it';
import { SH_CSS } from './start-here-css';

/**
 * START HERE: the chooser behind the header's "Start Here Free" (owner, 2026-10-22: "a beautiful modal in the fashion of our
 * exit intent trigger that would allow them to self select the demo path or the Build Now / Try it Free path"). Two ways in,
 * side by side: unlock the system now (try.leadpops.com, instant access, no credit card: the one lime action) or talk to our
 * team (the strategy session, the demo request page). Same motion as the v2 exit popup; a bottom sheet on a phone.
 *
 * Opens from the header key (StartHereCta) and from ANY element carrying `data-start-here` (so later every CTA on the site can
 * open it with one attribute, no new code). A link carrying `data-try-link` gets the visitor's light or dark added to its
 * try.leadpops.com address as it is pressed, so they land in the look they were reading in.
 *
 * Hidden-page preview for now: mounted by the header only on START_HERE_PATH.
 */
export const START_HERE_PATH = '/home-start-here';
export const START_HERE_LABEL = 'Start Here Free';

const CLOSE_MS = 700;

/** The chooser's words (owner, 2026-10-22: "Choose your path", "Show me the System", sell the Try experience, not the site). */
const COPY = {
  tag: 'Start here',
  headline: 'Choose your path.',
  line: 'Both free. Both end with more of your own leads.',
  build: {
    eyebrow: 'Instant access',
    title: 'See the system. Make it yours.',
    text: 'A real, working site and the system behind it: landing pages, quote builder, calculators, smart forms. Put your name and colors on it in seconds.',
    checks: ['Instant access. No credit card', 'No account. No login', 'Change anything, see it instantly'],
    cta: 'Show me the system',
  },
  talk: {
    eyebrow: 'Done for you',
    title: 'Talk to our team',
    text: 'A 30-minute strategy session. We map out your growth plan and build the complete system with you.',
    checks: ['Your blueprint, not a pitch', 'Full implementation included', "If we're not a fit, we'll tell you straight"],
    cta: 'Book a strategy call',
    href: '/mortgage-lead-generation-demo',
  },
  note: 'Not sure? Start with the system. You can book a call from inside it any time.',
} as const;

const Check = () => <svg viewBox="0 0 20 20" aria-hidden><path d="M4.5 10.5l3.5 3.5 7.5-8" /></svg>;
const Arrow = () => <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden><path d="M4 10h11M11 5l5 5-5 5" strokeLinecap="round" strokeLinejoin="round" /></svg>;

const track = (action: string) => {
  const g = (window as unknown as { gtag?: (...a: unknown[]) => void }).gtag;
  g?.('event', 'start_here', { action, page_path: window.location.pathname });
};

/** The visitor's look right now (the header's switch can change it after load), for the try link. */
const lookNow = () => tryItHref(document.documentElement.getAttribute('data-skin-mode'));

export function StartHereModal() {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<'in' | 'out'>('in');
  const openRef = useRef(false);
  const lastFocus = useRef<HTMLElement | null>(null);
  const first = useRef<HTMLAnchorElement>(null);
  const [tryHref, setTryHref] = useState<string>(TRY_IT.url);

  const show = useCallback(() => {
    if (openRef.current) return;
    openRef.current = true;
    lastFocus.current = document.activeElement as HTMLElement | null;
    setTryHref(lookNow());
    setState('in');
    setOpen(true);
    track('shown');
  }, []);

  const close = useCallback((how: 'closed' | 'build' | 'talk' = 'closed') => {
    if (!openRef.current || state === 'out') return;
    track(how);
    setState('out');
    window.setTimeout(() => { setOpen(false); openRef.current = false; lastFocus.current?.focus?.({ preventScroll: true }); }, CLOSE_MS);
  }, [state]);

  // any element carrying data-start-here opens the chooser; a try link gets the visitor's look as it is pressed
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const t = e.target instanceof Element ? e.target : null;
      const opener = t?.closest<HTMLElement>('[data-start-here]');
      if (opener) { e.preventDefault(); show(); return; }
      const tryLink = t?.closest<HTMLAnchorElement>('a[data-try-link]');
      if (tryLink) tryLink.href = lookNow();
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [show]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    // lock the page behind, keeping the scrollbar's space so the page never jumps sideways on Windows
    const html = document.documentElement, prev = html.style.overflow, prevGutter = html.style.scrollbarGutter;
    html.style.scrollbarGutter = 'stable';
    html.style.overflow = 'hidden';
    const f = window.setTimeout(() => first.current?.focus({ preventScroll: true }), 1400);
    return () => { document.removeEventListener('keydown', onKey); html.style.overflow = prev; html.style.scrollbarGutter = prevGutter; window.clearTimeout(f); };
  }, [open, close]);

  if (!open) return null;
  const mode = document.documentElement.getAttribute('data-skin-mode') === 'light' ? 'light' : 'dark';
  const words = COPY.headline.split(' ');
  const path = (p: 0 | 1, hi: boolean, c: { eyebrow: string; title: string; text: string; checks: readonly string[]; cta: string }, icon: ReactNode, action: ReactNode) => (
    <div className="sh-path" data-hi={hi ? '' : undefined} style={{ ['--p' as string]: p }}>
      <div className="sh-path-eye"><span className="sh-path-ico">{icon}</span>{c.eyebrow}</div>
      <p className="sh-path-t">{c.title}</p>
      <p className="sh-path-p">{c.text}</p>
      <ul className="sh-checks">{c.checks.map((k, i) => <li key={k} style={{ ['--r' as string]: i }}><Check />{k}</li>)}</ul>
      <div className="sh-act">{action}</div>
    </div>
  );
  // drawn on the page body, never inside the header: the header is its own stacking layer, so anything fixed later in the page
  // (the social-proof popup) would draw over the chooser
  return createPortal(
    <div className="sh" data-state={state} data-mode={mode}>
      <style>{SH_CSS}</style>
      <div className="sh-veil" onClick={() => close()} aria-hidden="true" />
      <div className="sh-beam" aria-hidden="true"><i /></div>
      <div className="sh-card" role="dialog" aria-modal="true" aria-labelledby="sh-h" aria-describedby="sh-line">
        <div className="sh-ring" aria-hidden="true" />
        <div className="sh-sun" aria-hidden="true" />
        <button type="button" className="sh-x" onClick={() => close()} aria-label="Close">
          <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><path d="M5 5l10 10M15 5L5 15" strokeLinecap="round" /></svg>
        </button>
        <div className="sh-s" style={{ ['--d' as string]: '0ms' }}><span className="sh-tag"><i />{COPY.tag}</span></div>
        <h2 id="sh-h" className="sh-h">
          {words.map((w, i) => (<span key={i}><span className="sh-w" style={{ ['--i' as string]: i }}>{w}</span>{i < words.length - 1 ? ' ' : ''}</span>))}
        </h2>
        <p id="sh-line" className="sh-line sh-s" style={{ ['--d' as string]: '260ms' }}>{COPY.line}</p>
        <div className="sh-paths">
          {path(0, true, COPY.build,
            <svg viewBox="0 0 20 20" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden><path d="M11 2 4 11h5l-1 7 7-9h-5l1-7Z" strokeLinejoin="round" /></svg>,
            <a ref={first} className="sh-cta" href={tryHref} onClick={() => track('build')}>{COPY.build.cta}<Arrow /></a>)}
          {path(1, false, COPY.talk,
            <svg viewBox="0 0 20 20" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden><path d="M4 5h12a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H9l-4 3v-3H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z" strokeLinejoin="round" /></svg>,
            <a className="sh-alt" href={COPY.talk.href} onClick={() => track('talk')}>{COPY.talk.cta}<Arrow /></a>)}
        </div>
        <p className="sh-note sh-s" style={{ ['--d' as string]: '760ms' }}>{COPY.note}</p>
        <div className="sh-foot sh-s" style={{ ['--d' as string]: '860ms' }}>
          <b>4.9</b><span className="sh-stars" aria-label="4.9 out of 5 stars">★★★★★</span>
          <span>750+ Google reviews</span><span className="sh-dot" aria-hidden="true" /><span>5,247+ active users</span><span className="sh-dot" aria-hidden="true" /><span>15+ years</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * The header's key on the Start Here page: the same lime key as Book Strategy Call (it keeps that link's classes, which the game
 * design styles), but it opens the chooser. Without script it is a link to the page's own "Where do you want to start?" band.
 */
export function StartHereCta({ className, children }: { className: string; children?: ReactNode }) {
  return (
    <a href="#start" className={className} data-start-here="" aria-haspopup="dialog">
      {START_HERE_LABEL}
      {children}
    </a>
  );
}
